import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Secours session pour les WebViews TV/mobile qui perdent les cookies à l'extinction :
// les jetons sont aussi gardés en localStorage et envoyés en header Authorization
// (le cookie httpOnly reste prioritaire côté serveur pour les navigateurs classiques).
export const saveTokens = (data) => {
  try {
    if (data?.token) localStorage.setItem('maya_at', data.token);
    if (data?.refresh_token) localStorage.setItem('maya_rt', data.refresh_token);
  } catch { /* stockage indisponible */ }
};
const clearTokens = () => {
  try { localStorage.removeItem('maya_at'); localStorage.removeItem('maya_rt'); } catch { /* ignore */ }
};
const storedToken = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };

// Contournement proxy production : certains proxys bloquent PUT/DELETE.
// On les envoie en POST avec l'en-tête X-HTTP-Method-Override (réécrit côté backend).
axios.interceptors.request.use((config) => {
  const m = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  const isApi = url.startsWith(API) || url.startsWith('/api');
  if (m === 'put' || m === 'delete' || m === 'patch') {
    if (isApi) {
      config.headers = config.headers || {};
      config.headers['X-HTTP-Method-Override'] = m.toUpperCase();
      config.method = 'post';
    }
  }
  if (isApi && !config.headers?.Authorization) {
    const at = storedToken('maya_at');
    if (at) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${at}`;
    }
  }
  if (m === 'get' && (config.url || '').includes('/contents')) {
    try {
      const p = JSON.parse(sessionStorage.getItem('activeProfile') || 'null');
      if (p?.kind === 'kids') config.params = { ...(config.params || {}), kids: 1 };
    } catch { /* ignore */ }
  }
  return config;
});

// Session persistante : si une requête échoue en 401 (jeton d'accès expiré),
// on tente un rafraîchissement silencieux via le refresh token puis on rejoue la requête.
// Seule une déconnexion volontaire (logout) met fin à la session.
let refreshPromise = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const url = config.url || '';
    const isAuthRoute = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'].some(p => url.includes(p));
    if (status === 401 && !config.__retried && !isAuthRoute && (url.startsWith(API) || url.startsWith('/api'))) {
      config.__retried = true;
      refreshPromise = refreshPromise || axios.post(`${API}/auth/refresh`,
        { refresh_token: storedToken('maya_rt') }, { withCredentials: true })
        .then((res) => { saveTokens(res.data); return res; })
        .finally(() => { refreshPromise = null; });
      try {
        await refreshPromise;
        // rejouer la requête avec le nouveau jeton (l'ancien header Bearer est périmé)
        if (config.headers?.Authorization) {
          const at = storedToken('maya_at');
          if (at) config.headers.Authorization = `Bearer ${at}`;
        }
        return axios(config);
      } catch { /* refresh impossible : session réellement expirée */ }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeProfile, setActiveProfileState] = useState(() => {
    try { const s = sessionStorage.getItem('activeProfile'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [profileVersion, setProfileVersion] = useState(0);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const setActiveProfile = useCallback(async (p) => {
    if (p) {
      // Fetch fresh user data to get the latest profile with all settings
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        const freshProfile = data.profiles?.find(pr => pr.id === p.id) || p;
        setActiveProfileState(freshProfile);
        sessionStorage.setItem('activeProfile', JSON.stringify(freshProfile));
      } catch {
        setActiveProfileState(p);
        sessionStorage.setItem('activeProfile', JSON.stringify(p));
      }
    } else {
      setActiveProfileState(null);
      sessionStorage.removeItem('activeProfile');
    }
    // Bump version to force re-render of profile-dependent components
    setProfileVersion(v => v + 1);
  }, []);

  // Keep activeProfile in sync when user data changes (after settings save)
  useEffect(() => {
    if (user && activeProfile && user.profiles) {
      const fresh = user.profiles.find(pr => pr.id === activeProfile.id);
      if (fresh) {
        const freshStr = JSON.stringify(fresh);
        const currentStr = JSON.stringify(activeProfile);
        if (freshStr !== currentStr) {
          setActiveProfileState(fresh);
          sessionStorage.setItem('activeProfile', JSON.stringify(fresh));
          setProfileVersion(v => v + 1);
        }
      }
    }
  }, [user]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    saveTokens(data);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    saveTokens(data);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    clearTokens();
    setUser(false);
    setActiveProfileState(null);
    sessionStorage.removeItem('activeProfile');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, activeProfile, setActiveProfile, profileVersion }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
